import { RouteDataService } from './../../../services/route-data.service';
import { EventEmitter, Injectable, NgZone, Renderer2, RendererFactory2 } from '@angular/core';
import { TrayService } from './tray/tray.service';
import { BsModalService, BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import { DraggableModal } from './draggable-modal.class';


export interface RegisteredModal {
  id: string;
  trayTitle?: string;
  component: Function;
  instance?: DraggableModal;
  modalService: BsModalService;
  initialState?: any;
}

export interface DraggableModalOptions extends ModalOptions {
  id: string;
  trayTitle?: string;
  baseId?: string;
  idSuffix?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  minWidth?: number;
  minHeight?: number;
}

export interface OnModalResize {
  onModalResize(width: number, height: number): void;
}

@Injectable()
export class DraggableModalService {
  modalOpened = new EventEmitter<DraggableModal>();
  private registeredModals: RegisteredModal[] = [];
  private renderer: Renderer2;
  constructor(private trayService: TrayService, rendererFactory: RendererFactory2, private zone: NgZone, private routerDataService: RouteDataService) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.trayService.maximizeModal.subscribe((options) => {
      const registeredModal = this.findModal(options.id, options.baseId);
      if (!registeredModal) {
        console.error(`Modal with id '${options.id}' not registered.  This may happen if you're trying to open a modal from lazy-loaded module that hasn't been loaded yet.`);
        return;
      }
      this.trayService.removeModalFromTray(options.id);
      if (!registeredModal.instance) {
        this.show(options);
        return;
      }
      if (registeredModal.instance.modalRef) {
        registeredModal.instance.showModalFromTray();
        return;
      }
      registeredModal.instance.openModal(registeredModal.component, options);
    });
    this.routerDataService.logout.subscribe((data: any) => {
      const openedModals = this.registeredModals.filter(modal => modal.instance).map(modal => modal.instance).reverse();
      openedModals.forEach((modal: DraggableModal) => {
        modal.modalRef.content.hide();
      });
      this.trayService.removeAllModals();
    });
  }

  getModals(): RegisteredModal[] {
    return this.registeredModals;
  }

  show(options: DraggableModalOptions): BsModalRef {
    const registeredModal = this.findModal(options.id, options.baseId);
    if (!registeredModal) {
      console.error(`Modal ${options.id} is not registered`);
      return;
    }
    if (registeredModal.instance && registeredModal.instance.modalRef) {
      if (options.initialState) {
        Object.assign(registeredModal.instance.modalRef.content, options.initialState);
      }
      registeredModal.instance.showModalFromTray();
      this.trayService.removeModalFromTray(registeredModal.id);
      return registeredModal.instance.modalRef;
    }

    if (this.trayService.findModal(registeredModal.id)) {
      const modalCoordinates = this.trayService.findModal(registeredModal.id);
      options.x = modalCoordinates.x;
      options.y = modalCoordinates.y;
      options.width = modalCoordinates.width;
      options.height = modalCoordinates.height;
      this.trayService.removeModalFromTray(registeredModal.id);
    }
    this.updateRegisteredModal(registeredModal.id, options);
    const modal = new DraggableModal(registeredModal.modalService, this, this.renderer, this.trayService, this.zone);
    modal.id = registeredModal.id;
    modal.trayTitle = registeredModal.trayTitle;
    registeredModal.instance = modal;
    modal.onHidden = () => {
      registeredModal.instance = null;
    };

    return modal.openModal(registeredModal.component, options);
  }

  register(id: string, component: Function, modalService: BsModalService): void {
    if (!this.findModal(id)) {
      this.registeredModals.push({id, component, modalService});
    }
  }

  updateRegisteredModal(id: string, options: DraggableModalOptions) {
    // const _options = JSON.parse(JSON.stringify(options));
    const modal = this.findModal(id);
    // if (_options.initialState) {
    //   modal.initialState = Object.assign({}, modal.initialState || {}, _options.initialState);
    //   delete _options.initialState;
    // }
    // this.registeredModals[this.registeredModals.indexOf(modal)] = Object.assign({}, modal, _options);
    this.registeredModals[this.registeredModals.indexOf(modal)] = Object.assign(modal, options);
  }

  findModal(id: string, baseId = ''): RegisteredModal {
    const registeredModal = this.registeredModals.find(modal => modal.id === id);
    const baseModal = this.registeredModals.find(modal => modal.id === baseId);
    if (registeredModal) {
      return registeredModal;
    }
    if (!baseModal) {
      return null;
    }
    this.register(id, baseModal.component, baseModal.modalService);

    return this.registeredModals[this.registeredModals.length - 1];
  }
}

